const FEATURE_FLAGS_KEY = 'clips_feature_flags';

export interface FeatureFlags {
  debugTimeouts: boolean;
  searchPage: boolean;
  feedbackTestMode: boolean;
  /** Override: show Catalog in studio even when app set has no CatalogEnabled backend flag */
  catalogEnabled: boolean;
}

const defaultFlags: FeatureFlags = {
  debugTimeouts: false,
  searchPage: false,
  feedbackTestMode: true,
  catalogEnabled: false,
};

class FeatureFlagManager {
  private flags: FeatureFlags;

  constructor() {
    this.flags = this.loadFlags();
    this.exposeToConsole();
  }

  private loadFlags(): FeatureFlags {
    try {
      const stored = localStorage.getItem(FEATURE_FLAGS_KEY);
      if (stored) {
        return { ...defaultFlags, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.warn('Failed to load feature flags from localStorage:', error);
    }
    return { ...defaultFlags };
  }

  private saveFlags(): void {
    try {
      localStorage.setItem(FEATURE_FLAGS_KEY, JSON.stringify(this.flags));
    } catch (error) {
      console.warn('Failed to save feature flags to localStorage:', error);
    }
  }

  private exposeToConsole(): void {
    // Expose feature flag controls to the developer console
    (window as any).clipsFeatureFlags = {
      get: () => ({ ...this.flags }),
      set: (flags: Partial<FeatureFlags>) => {
        this.flags = { ...this.flags, ...flags };
        this.saveFlags();
        console.log('Feature flags updated:', this.flags);
        // Dispatch a custom event to notify components of flag changes
        window.dispatchEvent(
          new CustomEvent('featureFlagsChanged', { detail: this.flags }),
        );
      },
      reset: () => {
        this.flags = { ...defaultFlags };
        this.saveFlags();
        console.log('Feature flags reset to defaults:', this.flags);
        window.dispatchEvent(
          new CustomEvent('featureFlagsChanged', { detail: this.flags }),
        );
      },
    };

    console.log('🎛️  Clips Feature Flags available in console:');
    console.log('  clipsFeatureFlags.get() - Get current flags');
    console.log(
      '  clipsFeatureFlags.set({ debugTimeouts: true }) - Enable debug timeouts UI',
    );
    console.log(
      '  clipsFeatureFlags.set({ catalogEnabled: true }) - Show Catalog in studio (override backend)',
    );
    console.log('  clipsFeatureFlags.reset() - Reset to defaults');
  }

  isEnabled(flag: keyof FeatureFlags): boolean {
    return Boolean(this.flags[flag]);
  }

  set(flags: Partial<FeatureFlags>): void {
    this.flags = { ...this.flags, ...flags };
    this.saveFlags();
  }

  getAllFlags(): FeatureFlags {
    return { ...this.flags };
  }
}

// Create singleton instance
export const featureFlags = new FeatureFlagManager();

// Export the flag names for type safety
export const FEATURE_FLAGS = {
  DEBUG_TIMEOUTS: 'debugTimeouts',
  SEARCH_PAGE: 'searchPage',
  FEEDBACK_TEST_MODE: 'feedbackTestMode',
  CATALOG_ENABLED: 'catalogEnabled',
} as const;

export type FeatureFlagName = keyof FeatureFlags;
